import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDebouncedCallback } from '@sorare/core/src/hooks/useDebouncedCallback';
import { glossary, scoutingCenter } from '@sorare/core/src/lib/glossary';

import { WatchlistItem } from 'components/watchlists/WatchlistItem';
import { WatchlistsHeader } from 'components/watchlists/WatchlistsHeader';
import { getSubscribableWatchlistTypes } from 'hooks/useHasFollowedPlayers';

import { FavoritesWatchlistItem } from '../FavoritesWatchlistItem';
import { SorareWatchlistBanner } from '../SorareWatchlistBanner';
import { EmptyState } from './EmptyState';
import type {
  WatchlistsPageQuery,
  WatchlistsPageQueryVariables,
} from './__generated__/index.graphql';

const WATCHLISTS_PAGE_QUERY = gql`
  query WatchlistsPageQuery(
    $sport: Sport!
    $query: String!
    $types: [SubscribableType!]
  ) {
    currentUser {
      slug
      myWatchlists(query: $query, sport: $sport) {
        slug
        id
        ...WatchlistItem_watchlist
      }
      mySubscriptions(types: $types) {
        totalCount
      }
    }
    market {
      id
      sorareWatchlists(sport: $sport) {
        id
        ...SorareWatchlistBanner_watchlist
      }
    }
  }
  ${WatchlistItem.fragments.watchlist}
  ${SorareWatchlistBanner.fragments.watchlist}
` as TypedDocumentNode<WatchlistsPageQuery, WatchlistsPageQueryVariables>;

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;
const SearchContainer = styled(Vertical).attrs({ gap: 1 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
`;
const WatchlistItems = styled(Vertical).attrs({ gap: 1 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  column-gap: var(--double-unit);
  row-gap: var(--triple-unit);
`;

type Watchlists = NonNullable<
  NonNullable<WatchlistsPageQuery['currentUser']>['myWatchlists']
>;

type Props = {
  sport: Sport;
};

export const WatchlistsPage = ({ sport }: Props) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState(search);
  const [watchlists, setWatchlists] = useState<Watchlists>([]);
  const { formatMessage } = useIntlContext();

  const { data, refetch } = useQuery(WATCHLISTS_PAGE_QUERY, {
    variables: {
      sport,
      query,
      types: getSubscribableWatchlistTypes(sport),
    },
    onCompleted: d => {
      setWatchlists(d?.currentUser?.myWatchlists || []);
    },
  });

  const debouncedSetQuery = useDebouncedCallback(
    (s: string) => setQuery(s),
    400
  );
  const favoritesCount = data?.currentUser?.mySubscriptions.totalCount || 0;
  const sorareWatchlists = data?.market?.sorareWatchlists || [];
  return (
    <>
      <PageHeader text={scoutingCenter.scout} disableBackBrowser />
      <Vertical>
        <WatchlistsHeader onAddWatchlist={refetch} />
        <SorareWatchlistBanner
          sorareWatchlists={sorareWatchlists}
          withContainer
        />
        <Content>
          <SearchContainer>
            <SearchInput
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                debouncedSetQuery(e.target.value);
              }}
              placeholder={formatMessage(glossary.search)}
              withIcon
            />
          </SearchContainer>
          <WatchlistItems>
            <FavoritesWatchlistItem count={favoritesCount} />
            {watchlists.length ? (
              watchlists.map(w => (
                <WatchlistItem key={w.slug} watchlist={w} onRemove={refetch} />
              ))
            ) : (
              <EmptyState onAddWatchlist={refetch} search={search} />
            )}
          </WatchlistItems>
        </Content>
      </Vertical>
    </>
  );
};
